/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { t } from "i18next";
import moment from "moment";

/**
 * Converts a Date object to ISO YYYY-MM-DD format
 * @param date - The date to format
 * @returns The date string in YYYY-MM-DD format
 */
export const getFromISOYYYYMMDDDateFormat = (date: Date) => {
  return date.toISOString().split("T")[0];
};

/**
 * Converts a Date object to DD/MM/YYYY format
 * @param date - The date to format
 * @returns The date string in DD/MM/YYYY format
 */
export const getFromISODDMMYYYYDateFormat = (date: Date): string => {
  const iso = getFromISOYYYYMMDDDateFormat(date);
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
};

/**
 * Extracts time in HH:MM:SS format from a Date object
 * @param date - The date to extract time from
 * @returns The time string in HH:MM:SS format
 */
export const getFromISOHHMMSSTimeFormat = (date: Date) => {
  return date.toISOString().split("T")[1].split(".")[0];
};

/**
 * Converts a Date object to ISO YYYY-MM-DDTHH:MM:SS format
 * @param date - The date to format
 * @returns The datetime string in ISO YYYY-MM-DDTHH:MM:SS format
 */
export const getFromISOISOYYYYMMDDTHHMMSSTimeFormat = (date: Date) => {
  return new Date(date).toISOString().split(".")[0];
};

/**
 * Formats an appointment time range as a string with start and end times
 * @param start - The start date/time of the appointment
 * @param end - The end date/time of the appointment
 * @returns A formatted time range string (e.g., "09:00 am - 10:30 am")
 */
export const getAppointmentTimeRange = (start: Date, end: Date) => {
  const startTimeStr = moment(start).format("hh:mm a");
  const endTimeStr = moment(end).format("hh:mm a");
  return `${startTimeStr} - ${endTimeStr}`;
};

/**
 * Sets a date to the start of the day (00:00:00) and returns as ISO string
 * @param startDate - The date to set to start of day
 * @returns The ISO string representation of the date at 00:00:00
 */
export const getStartISODateTimeRange = (startDate: Date) => {
  const date = new Date(startDate);
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  return date.toISOString();
};

/**
 * Sets a date to the end of the day (23:59:59) and returns as ISO string
 * @param endDate - The date to set to end of day
 * @returns The ISO string representation of the date at 23:59:59
 */
export const getEndISODateTimeRange = (endDate: Date) => {
  const date = new Date(endDate);
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  return date.toISOString();
};

/**
 * Calculates the age in years from a date of birth
 * @param dateOfBirth - The date of birth as a string or Date object
 * @returns The calculated age in years
 */
export function calculateAge(dateOfBirth: string | Date): number {
  const dob = new Date(dateOfBirth);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

/**
 * Formats a date string into a human-readable relative time message
 * @param dateString - The date string to format
 * @returns A formatted string indicating time elapsed (e.g., "5 minutes ago", "2 hours ago")
 */
export const formatDateMessages = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffHours < 1) {
      return (diffMinutes < 1)
        ? t("notificationWidget.now")
        : `${diffMinutes} ${t(diffMinutes === 1 ? "notificationWidget.minuteAgo" : "notificationWidget.minutesAgo")}`;
    } else if (diffHours < 24) {
      return `${diffHours} ${t(diffHours === 1 ? "notificationWidget.hourAgo" : "notificationWidget.hoursAgo")}`;
    } else if (diffDays < 7) {
      return `${diffDays} ${t(diffDays === 1 ? "notificationWidget.dayAgo" : "notificationWidget.daysAgo")}`;
    } else {
      return date.toLocaleDateString();
    }
  } catch (e) {
    return (t("notificationWidget.invalidDate"));
  }
};

/**
 * Formats a date for API consumption with optional end-of-day adjustment
 * @param date - The date to format
 * @param isEndDate - If true, sets time to 23:59:59.999; if false, sets to 00:00:00.000
 * @returns The formatted date as an ISO string, or empty string if date is invalid
 */
export const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
  if (!date) return "";

  const localDate = new Date(date);

  if (isEndDate) {
    localDate.setHours(23, 59, 59, 999);
  } else {
    localDate.setHours(0, 0, 0, 0);
  }

  return localDate.toISOString();
};

/**
 * Formats a date as a time string for chat display in 24-hour format
 * @param date - The date to format
 * @returns The time string in HH:MM format (e.g., "14:30")
 */
export const formatTimeForChat = (date: Date): string => {
  return date.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Creates a safe Date object with consistent time handling to avoid timezone issues
 * @param inputDate - The input date to process
 * @param isEndOfDay - If true, creates date at 23:59:59.999; if false, at 00:00:00.000
 * @returns A new Date object with consistent time values
 */
export const createSafeDate = (inputDate: Date, isEndOfDay: boolean = false): Date => {
  const year = new Date(inputDate).getFullYear();
  const month = new Date(inputDate).getMonth();
  const day = new Date(inputDate).getDate();
    
  if (isEndOfDay) {
    return new Date(year, month, day, 23, 59, 59, 999);
  } else {
    return new Date(year, month, day, 0, 0, 0, 0);
  }
};

/**
 * Formats a date for display in DD/MM/YYYY format using Mexican locale
 * @param dateInput - The date to format as a string or Date object
 * @returns The formatted date string in DD/MM/YYYY format, or empty string if invalid
 */
export const formatDateForDisplay = (dateInput: string | Date): string => {
  if (!dateInput) return "";
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return "";
  }
};

/**
 * Verifies if a person is a minor (under 21 years old) based on their date of birth
 * @param dob - The date of birth to check
 * @returns True if the person is under 21 years old, false otherwise
 */
export const verifyIsMinor = (dob: Date) => {
  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear();
  const month = today.getMonth() - dob.getMonth();
  const day = today.getDate() - dob.getDate();
  if (month < 0 || (month === 0 && day < 0)) {
    return false;
  }
  return age < 21;
};

/**
 * Calculates the difference in days between two dates
 * @param endDate - The end date
 * @param startDate - The start date
 * @returns The number of days between the two dates
 */
export const getDaysDifference = (endDate: Date, startDate: Date): number => {
  return moment(endDate).diff(moment(startDate), "days");
};

/**
 * Formats a date in YYYY-MM-DD format
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatDateKey = (date: Date | string): string => {
  return moment(date).format("YYYY-MM-DD");
};

/**
 * Formats a date in full readable format (e.g., "Monday, January 15, 2025")
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatFullDate = (date: Date | string): string => {
  return moment(date).format("dddd, MMMM D, YYYY");
};

/**
 * Formats a date in short month and day format (e.g., "Jan 15")
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatShortDate = (date: Date | string): string => {
  return moment(date).format("MMM D");
};

/**
 * Formats a date in short month, day and year format (e.g., "Jan 15, 2025")
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatShortDateWithYear = (date: Date | string): string => {
  return moment(date).format("MMM D, YYYY");
};

/**
 * Formats a date range as a string (e.g., "Jan 15 - Jan 20, 2025")
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns The formatted date range string
 */
export const formatDateRange = (startDate: Date | string, endDate: Date | string): string => {
  const start = moment(startDate);
  const end = moment(endDate);
  return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
};

/**
 * Formats a date in YYYY-MM format (for month grouping)
 * @param date - The date to format
 * @returns The formatted month key
 */
export const formatMonthKey = (date: Date | string): string => {
  return moment(date).format("YYYY-MM");
};

/**
 * Formats a date in YYYY-WW format (for week grouping)
 * @param date - The date to format
 * @returns The formatted week key
 */
export const formatWeekKey = (date: Date | string): string => {
  return moment(date).format("YYYY-WW");
};

/**
 * Gets the start of the week for a given date
 * @param date - The date to get week start from
 * @returns The Date object representing the start of the week
 */
export const getWeekStart = (date: Date | string): Date => {
  return moment(date).startOf("week").toDate();
};

/**
 * Gets the end of the week for a given date
 * @param date - The date to get week end from
 * @returns The Date object representing the end of the week
 */
export const getWeekEnd = (date: Date | string): Date => {
  return moment(date).endOf("week").toDate();
};

/**
 * Gets the start of the month for a given date
 * @param date - The date to get month start from
 * @returns The Date object representing the start of the month
 */
export const getMonthStart = (date: Date | string): Date => {
  return moment(date).startOf("month").toDate();
};

/**
 * Gets the end of the month for a given date
 * @param date - The date to get month end from
 * @returns The Date object representing the end of the month
 */
export const getMonthEnd = (date: Date | string): Date => {
  return moment(date).endOf("month").toDate();
};

/**
 * Checks if a date is before another date
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns True if date1 is before date2
 */
export const isDateBefore = (date1: Date | string, date2: Date | string): boolean => {
  return moment(date1).isBefore(moment(date2));
};

/**
 * Checks if a date is after another date
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns True if date1 is after date2
 */
export const isDateAfter = (date1: Date | string, date2: Date | string): boolean => {
  return moment(date1).isAfter(moment(date2));
};

/**
 * Compares two dates and returns the difference in the specified unit
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns The difference in milliseconds
 */
export const compareDates = (date1: Date | string, date2: Date | string): number => {
  return moment(date1).diff(moment(date2));
};
